import type { StatusUpdateCadence } from './detectStatusUpdateCadence.ts';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** A cadence needs at least this many consecutive dates on the same rhythm. */
const MIN_CONSECUTIVE_DATES = 3;

/**
 * Inclusive gap windows (in days) for each cadence, kept tight around a true 7-,
 * 14-, ~30- and ~91-day rhythm to avoid false positives. A cadence needs two
 * back-to-back gaps in the same window, so the intentional spacing between
 * windows — and, because every window starts well above zero, any zero gap (a
 * duplicate date) — never reads as a cadence.
 */
const CADENCE_WINDOWS: readonly {
	cadence: StatusUpdateCadence;
	minDays: number;
	maxDays: number;
}[] = [
	{ cadence: 'daily', minDays: 1, maxDays: 1 },
	{ cadence: 'weekly', minDays: 6, maxDays: 8 },
	{ cadence: 'biweekly', minDays: 13, maxDays: 15 },
	{ cadence: 'monthly', minDays: 28, maxDays: 32 },
	{ cadence: 'quarterly', minDays: 85, maxDays: 95 },
];

const toTimestamp = (date: string | number | Date): number => {
	if (date instanceof Date) {
		return date.getTime();
	}
	if (typeof date === 'number') {
		return date;
	}
	return new Date(date).getTime();
};

const daysBetween = (earlier: number, later: number): number => (later - earlier) / MS_PER_DAY;

/** The cadence a gap of the given length (in days) matches, if any. */
const cadenceForGap = (gapInDays: number): StatusUpdateCadence | undefined =>
	CADENCE_WINDOWS.find(({ minDays, maxDays }) => gapInDays >= minDays && gapInDays <= maxDays)
		?.cadence;

/**
 * Classifies a set of dates as a `weekly`, `biweekly`, `monthly` or `quarterly`
 * cadence, or `undefined` if a cadence is detected across MIN_CONSECUTIVE_DATES consecutive dates.
 */
export const detectDateCadence = (
	dates: readonly (string | number | Date)[] | null | undefined,
): StatusUpdateCadence | undefined => {
	const timestamps: number[] = [];
	for (const date of dates ?? []) {
		const value = toTimestamp(date);
		if (Number.isFinite(value)) {
			timestamps.push(value);
		}
	}
	timestamps.sort((a, b) => a - b);

	let cadence: StatusUpdateCadence | undefined;
	let datesInARow = 1;
	for (let index = 1; index < timestamps.length; index++) {
		const previousDay = timestamps[index - 1];
		const currentDay = timestamps[index];
		const gapCadence = cadenceForGap(daysBetween(previousDay, currentDay));

		if (gapCadence !== undefined && gapCadence === cadence) {
			datesInARow += 1;
		} else {
			// The run resets: a broken gap starts over at 1, a new rhythm at 2.
			datesInARow = gapCadence === undefined ? 1 : 2;
		}
		cadence = gapCadence;
		if (datesInARow >= MIN_CONSECUTIVE_DATES) {
			return cadence;
		}
	}
	return undefined;
};
